import { useTheme } from "@mui/material";
import ScalableImageWithCanvasOverlay, { ScalableCanvasDrawHandler, ScalableCanvasMouseEvent, ScalableCanvasTouchEvent } from "components/OptionsWizard/GenericComponents/ScalableImageWithCanvasOverlay";
import React, { useEffect } from 'react';
import { ICADState, useCADActions, useCADState } from "../Context/CADContext";
import { CADDrawingHelpers } from "../CADDrawingHelpers";
import { ICADLayer, ICADModel, IPoint } from "../interfaces";
import { CADHitTesting } from "../CADHitTesting";
import { CADActions } from "../Context/CADActions";
import CADOperationEditorAPI from "../API/CADOperationEditorAPI";
import { AddEdgeAdjustmentStateEnum } from "./EdgeTools/AddEdgeAdjustment";
import { CADDrawingUnitHelper } from "../CADDrawing/CADDrawingUnitHelper";
interface ICADDrawingProps {
    loadedImageUrl: string;
    imageScale: number;
    imageOffset: IPoint;
}

const DrawCanvas = (cadState: ICADState, ctx: CanvasRenderingContext2D | null, canvasBounds: DOMRect | null, unitHelper: CADDrawingUnitHelper) => {
    if (ctx && canvasBounds && cadState.model) {
        const drawingHelpers = new CADDrawingHelpers(ctx, canvasBounds, unitHelper);

        drawingHelpers.ClearCanvas();
        drawingHelpers.DrawHoveredOperation(cadState.hoveredOperationID, cadState.model, cadState.currentLayer);
        drawingHelpers.DrawSelectedOperation(cadState.selectedOperationID, cadState.model, cadState.currentLayer);
        drawingHelpers.DrawAddingOperation(cadState.operationEditor?.placement, cadState.operationEditor?.operation, cadState.model, cadState.currentLayer);
        drawingHelpers.DrawAddingEditingAdjustment(cadState.currentLayer, cadState.activeEdgeAdjustment);

    }
}

const InitCanvas = (ctx: CanvasRenderingContext2D, canvasBounds: DOMRect) => {
    ctx.translate(0, canvasBounds.height);

    // Scale the y-axis to be positive upwards
    ctx.scale(1, -1);
}

const HitTestOperationAndSetHover = (currentLayer: ICADLayer, model: ICADModel, modelPos: IPoint, cadActions: CADActions, unitHelper: CADDrawingUnitHelper) => {

    const layerOperations = model.operations.filter(op => op.layers.some(layer => layer.toLocaleLowerCase() === currentLayer.name.toLocaleLowerCase()));

    const operation = CADHitTesting.HitTestOperation(currentLayer, layerOperations, modelPos, unitHelper);

    if (operation) {
        cadActions.setHoveredOperation(operation.uniqueID);
        return true
    } else {
        return false;
    }
}

const HitTestEdgeAdjustmentsAndSetHover = (currentLayer: ICADLayer, modelPos: IPoint, cadActions: CADActions, unitHelper: CADDrawingUnitHelper): boolean => {

    const adjustment = CADHitTesting.HitTestEdgeAdjustments(currentLayer, modelPos, unitHelper);
    if (adjustment) {
        cadActions.setHoveredOperation(adjustment.uniqueID);
        return true;
    } else {
        return false;
    }
}

const CADDrawing: React.FC<ICADDrawingProps> = (props: ICADDrawingProps) => {

    const theme = useTheme();
    const cadState = useCADState();
    const cadActions = useCADActions();

    const [unitHelper, setUnitHelper] = React.useState<CADDrawingUnitHelper>(() => new CADDrawingUnitHelper(props.imageScale, props.imageOffset, 1, { x: 0, y: 0 }));
    const [refreshTrigger, setRefreshTrigger] = React.useState<boolean>(false);


    useEffect(() => {
        unitHelper.UpdateImageInfo(props.imageScale, props.imageOffset);
        setUnitHelper(unitHelper);
        setRefreshTrigger(t => !t); // Trigger a refresh to redraw the canvas with new image info
    }, [props.imageScale, props.imageOffset]);

    const handleCanvasStateChange: ScalableCanvasDrawHandler = React.useCallback((ctx, canvasBounds, ratio, offsetX, offsetY) => {

        //If we loaded a new image due to a change in visibility controls ( show dimensions, show edge numbers, etc. ) 
        // we need to redraw to ensure ensure selections are drawn in the correct place

        unitHelper.UpdateCanvasInfo(ratio, { x: offsetX, y: offsetY });
        setUnitHelper(unitHelper);

        DrawCanvas(cadState, ctx, canvasBounds, unitHelper);
    }, [cadState, props.imageScale, props.imageOffset]);

    const drawCanvasInternal: ScalableCanvasDrawHandler = React.useCallback((ctx, canvasBounds, ratio, offsetX, offsetY) => {
        DrawCanvas(cadState, ctx, canvasBounds, unitHelper);
    }, [cadState, unitHelper]);

    const handleMouseDown: ScalableCanvasMouseEvent = React.useCallback((e: React.MouseEvent<HTMLCanvasElement>, ctx: CanvasRenderingContext2D, canvasBounds: DOMRect, ratio: number, offsetX: number, offsetY: number) => {
        if (cadState.hoveredOperationID) {
            // get operation object from hoveredOperationID

            const op = CADDrawingHelpers.CADOperationFromUniqueID(cadState.hoveredOperationID, cadState.model?.operations);
            if (op) {
                cadActions.setSelectedOperation(op);
            } else {
                const adj = CADDrawingHelpers.EdgeAdjustmentFromUniqueID(cadState.hoveredOperationID, cadState.currentLayer);
                if (adj) {
                    cadActions.setActiveEdgeAdjustment(adj, AddEdgeAdjustmentStateEnum.None);
                } else {
                    cadActions.setSelectedOperation(null);
                }
            }
        } else {
            cadActions.setSelectedOperation(null);
            cadActions.setActiveEdgeAdjustment(null, AddEdgeAdjustmentStateEnum.None);
        }
    }, [cadState.hoveredOperationID, cadActions, cadState.model?.operations, cadState.currentLayer]);


    const handleMouseDoubleClick: ScalableCanvasMouseEvent = React.useCallback((e: React.MouseEvent<HTMLCanvasElement>, ctx: CanvasRenderingContext2D, canvasBounds: DOMRect, ratio: number, offsetX: number, offsetY: number) => {
        if (cadState.operationEditor === null && cadState.currentLayer && cadState.model) {
            if (cadState.selectedOperationID) {
                const op = CADDrawingHelpers.CADOperationFromUniqueID(cadState.selectedOperationID, cadState.model?.operations);
                if (op) {
                    CADOperationEditorAPI.InitExistingAsync(op.uniqueID).then((editor) => {
                        cadActions.setOperationEditor(editor);
                    });
                }
            } else if (cadState.activeEdgeAdjustment) {
                cadActions.setActiveEdgeAdjustment(cadState.activeEdgeAdjustment, AddEdgeAdjustmentStateEnum.EnterAdjustment);
            }
            e.preventDefault();
        }
    }, [cadState.operationEditor, cadState.currentLayer, cadState.model, cadState.selectedOperationID, cadState.activeEdgeAdjustment, cadActions]);


    const handleMouseMove: ScalableCanvasMouseEvent = React.useCallback((e: React.MouseEvent<HTMLCanvasElement>, ctx: CanvasRenderingContext2D, canvasBounds: DOMRect, ratio: number, offsetX: number, offsetY: number) => {

        if (!cadState.currentLayer) return;

        if (ctx && canvasBounds && cadState.model) {
            if (cadState.operationEditor === null) {
                //Normal mouse hover
                const drawingHelpers = new CADDrawingHelpers(ctx, canvasBounds, unitHelper);

                const modelPos = drawingHelpers.MousePositionInModelSpace(e);

                e.currentTarget.style.cursor = "pointer"; // Change cursor to pointer 

                // If Ctrl key is pressed, detect adjustments first ( to handle edges that have both edgework and adjustments)
                if (e.ctrlKey) {

                    if (!HitTestEdgeAdjustmentsAndSetHover(cadState.currentLayer, modelPos, cadActions, unitHelper)) {
                        // If no edge adjustment was hit, check for operations
                        if (!HitTestOperationAndSetHover(cadState.currentLayer, cadState.model, modelPos, cadActions, unitHelper)) {
                            // No operation or edge adjustment was hit
                            e.currentTarget.style.cursor = "default"; // Change cursor back to default
                            cadActions.setHoveredOperation(null);
                        }
                    }
                } else {

                    e.currentTarget.style.cursor = "pointer";
                    if (!HitTestOperationAndSetHover(cadState.currentLayer, cadState.model, modelPos, cadActions, unitHelper)) {
                        // If no operation was hit, check for edge adjustments
                        if (!HitTestEdgeAdjustmentsAndSetHover(cadState.currentLayer, modelPos, cadActions, unitHelper)) {
                            e.currentTarget.style.cursor = "default"; // Change cursor back to default
                            cadActions.setHoveredOperation(null);
                        }
                    }

                }
                DrawCanvas(cadState, ctx, canvasBounds, unitHelper);
            }
        }
    }, [cadState, cadActions, unitHelper]);

    const handleTouchStart: ScalableCanvasTouchEvent = React.useCallback((e: React.TouchEvent<HTMLCanvasElement>, ctx: CanvasRenderingContext2D, canvasBounds: DOMRect, ratio: number, offsetX: number, offsetY: number) => {

        // Prevent default touch behavior
        e.preventDefault();

        const touch = e.touches[0];
        e["clientX"] = touch.clientX;
        e["clientY"] = touch.clientY;

        // Handle touch start as mouse down
        handleMouseMove(e as unknown as React.MouseEvent<HTMLCanvasElement>, ctx, canvasBounds, ratio, offsetX, offsetY);

    }, [handleMouseDown, handleMouseMove]);


    return <>
        <ScalableImageWithCanvasOverlay
            src={props.loadedImageUrl}
            theme={theme}
            renderAsDiv={false}
            imageStyle={undefined}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => null}
            onTouchStart={handleTouchStart}
            drawCanvas={drawCanvasInternal}
            onInitCanvas={InitCanvas}
            onCanvasStateChange={handleCanvasStateChange}
            onMouseDoubleClick={handleMouseDoubleClick}
            refreshTrigger={refreshTrigger}
        />
    </>;

}

export default CADDrawing;
